/**
 * The currencies BolloPrint handles — ONE list for Admin + Tenant.
 *
 * Mirror of `shared/py/bolloprint_shared/currencies.py`; the backend test
 * `test_currencies.py` fails if the two drift apart. Read the Python module's
 * docstring for why the list is what it is (euro + the ECB's 29 + the national
 * currency of every country in the registry; Gibraltar in GBP).
 *
 * Names are NOT stored: `currencyName()` asks `Intl.DisplayNames` in the
 * screen's own language, so adding a currency needs no translation.
 */

export interface CurrencyInfo {
  code: string;
  /** Glyph printed after the amount. Several currencies share one (`kr`, `L`). */
  symbol: string;
  /** ISO 4217 minor unit: 0 for JPY, ISK, KRW and CLP, 2 for the rest. */
  decimals: number;
}

const c = (code: string, symbol: string, decimals = 2): CurrencyInfo => ({ code, symbol, decimals });

/** In the order of the Python list: EUR, the ECB's own order, then the rest. */
export const CURRENCY_LIST: readonly CurrencyInfo[] = [
  c('EUR', '€'),
  c('USD', '$'),
  c('JPY', '¥', 0),
  c('CZK', 'Kč'),
  c('DKK', 'kr'),
  c('GBP', '£'),
  c('HUF', 'Ft'),
  c('PLN', 'zł'),
  c('RON', 'lei'),
  c('SEK', 'kr'),
  c('CHF', 'SFr'),
  c('ISK', 'kr', 0),
  c('NOK', 'kr'),
  c('TRY', '₺'),
  c('AUD', 'A$'),
  c('BRL', 'R$'),
  c('CAD', 'C$'),
  c('CNY', 'CN¥'),
  c('HKD', 'HK$'),
  c('IDR', 'Rp'),
  c('ILS', '₪'),
  c('INR', '₹'),
  c('KRW', '₩', 0),
  c('MXN', 'MX$'),
  c('MYR', 'RM'),
  c('NZD', 'NZ$'),
  c('PHP', '₱'),
  c('SGD', 'S$'),
  c('THB', '฿'),
  c('ZAR', 'R'),
  c('ALL', 'L'),
  c('AMD', '֏'),
  c('ARS', 'AR$'),
  c('AZN', '₼'),
  c('BAM', 'KM'),
  c('CLP', 'CLP$', 0),
  c('COP', 'COL$'),
  c('DOP', 'RD$'),
  c('GEL', '₾'),
  c('KZT', '₸'),
  c('MDL', 'L'),
  c('MKD', 'ден'),
  c('PEN', 'S/'),
  c('RSD', 'дин'),
  c('UAH', '₴'),
  c('UYU', '$U'),
  c('VES', 'Bs.'),
];

export const DEFAULT_CURRENCY = 'EUR';

const BY_CODE: ReadonlyMap<string, CurrencyInfo> = new Map(CURRENCY_LIST.map((x) => [x.code, x]));

/** Symbols that more than one currency prints (`kr`, `L`). */
const AMBIGUOUS: ReadonlySet<string> = (() => {
  const seen = new Map<string, number>();
  for (const x of CURRENCY_LIST) seen.set(x.symbol, (seen.get(x.symbol) ?? 0) + 1);
  return new Set([...seen].filter(([, n]) => n > 1).map(([s]) => s));
})();

const norm = (code: string | null | undefined): string => (code || '').trim().toUpperCase();

/** The entry for `code` (case-insensitive), or `undefined`. */
export function currencyInfo(code: string | null | undefined): CurrencyInfo | undefined {
  return BY_CODE.get(norm(code));
}

export function isSupportedCurrency(code: string | null | undefined): boolean {
  return BY_CODE.has(norm(code));
}

/** ISO 4217 minor unit; 2 for anything unknown. */
export function currencyDecimals(code: string | null | undefined): number {
  return currencyInfo(code)?.decimals ?? 2;
}

/** Whether the symbol of `code` is shared with another currency of the list. */
export function hasAmbiguousSymbol(code: string | null | undefined): boolean {
  const info = currencyInfo(code);
  return !!info && AMBIGUOUS.has(info.symbol);
}

/**
 * The currency's name in `locale` («dólar estadounidense», «US Dollar»), from
 * the browser. Falls back to the code when the runtime does not know it.
 */
export function currencyName(code: string, locale: string): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(code);
    return name && name !== code ? name.charAt(0).toUpperCase() + name.slice(1) : code;
  } catch {
    return code;
  }
}

// ── Documents in another currency (documentation/Multi_Currency.md) ─────────

/** The frozen conversion a document carries, in either case of the wire. */
export interface FxStamp {
  currency?: string | null;
  baseCurrency?: string | null;
  fxRate?: number | null;
}

/**
 * An amount of a document in the workshop's CURRENT currency — the mirror of
 * the server's `convert.base_amount`. Same currency → the amount; a rate
 * stamped to `workshopCurrency` → amount × rate; anything else (no rate for
 * that day, or a rate to a currency the workshop no longer uses) → `null`,
 * which the screens show as «sin convertir», never as a guess.
 */
export function baseAmount(
  amount: number | null | undefined,
  doc: FxStamp,
  workshopCurrency: string,
): number | null {
  if (amount == null || !Number.isFinite(amount)) return null;
  const ws = workshopCurrency.trim().toUpperCase();
  const cur = (doc.currency || ws).trim().toUpperCase();
  if (cur === ws) return amount;
  if ((doc.baseCurrency || '').toUpperCase() === ws && doc.fxRate != null && doc.fxRate > 0) {
    const f = 10 ** (currencyInfo(ws)?.decimals ?? 2);
    return Math.round(amount * doc.fxRate * f) / f;
  }
  return null;
}
